import { getApiUrl } from "src/common/api/getUrl";
import { dataParser } from "src/common/dataParser";
import { mdmLoader } from "src/common/mdmLoader";
import { rest } from "src/common/rest";
import { RtLoaderFn } from "src/components/ReactiveTable/RtLoader";
import z from "zod";

import {
  ExtensionsFilter,
  ZExtension,
  zExtension,
  zExtensionCommon,
  ZExtensionScope,
} from "./ZExtension";

const urlExtensions = (path = "") => getApiUrl(`/core/extensions${path}`);

export const zExtensionWrite = zExtensionCommon.omit({ file: true }).extend({
  fileId: z.string().optional(),
});
export type ZExtensionWrite = z.infer<typeof zExtensionWrite>;

export const extensionsLoader = (): RtLoaderFn<ZExtension, ExtensionsFilter> =>
  mdmLoader(zExtension, urlExtensions());

export const loadExtension = async (id: string): Promise<ZExtension> => {
  const resp = await rest.get(urlExtensions(`/${id}`));
  return dataParser(() => zExtension.parse(resp.data), "loadExtension");
};

export const createExtension = async (
  body: ZExtensionWrite,
): Promise<ZExtension> => {
  const resp = await rest.post<ZExtension, ZExtensionWrite>(
    urlExtensions(),
    body,
  );
  return dataParser(() => zExtension.parse(resp.data), "createExtension");
};

export const updateExtension = async (
  id: string,
  body: ZExtensionWrite,
): Promise<ZExtension> => {
  const resp = await rest.put<ZExtension, ZExtensionWrite>(
    urlExtensions(`/${id}`),
    body,
  );
  return dataParser(() => zExtension.parse(resp.data), "updateExtension");
};

export const deleteExtension = async (id: string): Promise<void> => {
  await rest.delete(urlExtensions(`/${id}`));
};

export const extensionsByScopeLoader =
  (scope: ZExtensionScope): RtLoaderFn<ZExtension, ExtensionsFilter> =>
  async (request) => {
    const zResp = z.object({
      content: zExtension.array(),
      pagination: z.object({
        totalElements: z.number(),
      }),
    });
    const params = {
      scope,
      page: request.page,
      size: request.pageSize,
    };
    const resp = await rest.get(urlExtensions("/by-scope"), { params });
    const { content, pagination } = dataParser(
      () => zResp.parse(resp.data),
      "extensionsByScopeLoader",
    );
    return { rows: content, totalRows: pagination.totalElements };
  };
